/**
 * User-defined fields on a record — and the durable home for the two Wallet
 * fields AIM has no column for.
 *
 * Wallet's export carries `counterParty` and `recordState`. AIM's `Income` /
 * `Expense` tables carry neither. Rather than invent columns or drop the data,
 * this block renders whatever custom fields an admin has defined for this
 * record type (`/custom-fields`), so `Counterparty` (text) and `Record state`
 * (a choice list of Cleared / Pending) become real, queryable, exportable
 * values the moment somebody defines them — with no change to this screen.
 * Until then it renders nothing and costs nothing.
 *
 * WHY THIS IS NOT `components/custom-fields/CustomFieldInputs`
 * ------------------------------------------------------------
 * That component ships a hook designed for exactly this job, and it was the
 * first thing tried. Its seeding effect is
 *
 *     const { data: existingValues = [] } = useQuery({ …, enabled: Boolean(entityId) });
 *     useEffect(() => {
 *       if (!entityId) { setDraft({}); setTouched(new Set()); return; }
 *       …
 *     }, [entityId, existingValues]);
 *
 * With no `entityId` — i.e. on EVERY new record, which is the common case —
 * the query is disabled, `data` is `undefined`, and the `= []` default builds
 * a NEW array on every render. That array is an effect dependency, and the
 * effect unconditionally calls `setDraft({})` with a fresh object, so it
 * re-renders, re-defaults, and re-fires: an infinite render loop that hangs the
 * screen. Reproduced in a jsdom mount test before this file existed.
 *
 * The API layer (`components/custom-fields/api.ts`) is reused verbatim — the
 * bug is in the hook, not the fetchers — and the query keys are the same, so
 * this shares one cache with the admin manager and the records tab.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  fetchFields,
  fetchValues,
  paiseToRupees,
  rupeesToPaise,
  saveValues,
  type CustomField,
  type CustomFieldEntityType,
  type CustomFieldValue,
} from "@/components/custom-fields/api";
import { cn } from "@/lib/utils";

export interface ExtraFieldsState {
  fields: CustomField[];
  draft: Record<string, string>;
  setValue: (key: string, value: string) => void;
  errors: Record<string, string>;
  isValid: boolean;
  /** Write the touched values against a saved record id. */
  save: (entityId: string | undefined) => Promise<void>;
}

function draftFromValue(value: CustomFieldValue): string {
  if (value.value === null || value.value === undefined) return "";
  if (value.fieldType === "option_set") return value.optionId ?? "";
  if (value.fieldType === "number" && value.isMoney) return paiseToRupees(Number(value.value));
  return String(value.value);
}

export function useExtraFields(
  entityType: CustomFieldEntityType,
  entityId: string | undefined,
): ExtraFieldsState {
  const [draft, setDraft] = useState<Record<string, string>>({});
  // Only what the user actually touched is written, so an untouched form never
  // rewrites a value it merely displayed.
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // The OFFER list: no `includeArchived`, so a retired field is never offered
  // on a new entry.
  const fieldsQuery = useQuery({
    queryKey: ["custom-fields", entityType],
    queryFn: () => fetchFields({ entityType }),
    staleTime: 60_000,
  });

  const valuesQuery = useQuery({
    queryKey: ["custom-field-values", entityType, entityId],
    queryFn: () => fetchValues(entityType, entityId as string),
    enabled: Boolean(entityId),
  });

  /*
   * The seeding effect, and the reason it is written this way.
   *
   * It depends on `valuesQuery.data` — the query's OWN reference, which is
   * `undefined` while disabled and stable once resolved — and never on a
   * `= []` default, which would be a new array on every render and would turn
   * this effect into an infinite loop (see the module header). It also has no
   * "reset to empty" branch: switching to create mode leaves the draft alone,
   * which is what makes a DUPLICATE keep the values the user just typed.
   */
  useEffect(() => {
    const values = valuesQuery.data;
    if (!entityId || !values) return;
    const seeded: Record<string, string> = {};
    for (const value of values) seeded[value.fieldKey] = draftFromValue(value);
    setDraft(seeded);
    setTouched(new Set());
  }, [entityId, valuesQuery.data]);

  const fields = useMemo(() => fieldsQuery.data ?? [], [fieldsQuery.data]);

  const errors = useMemo(() => {
    const found: Record<string, string> = {};
    for (const field of fields) {
      const raw = (draft[field.key] ?? "").trim();
      if (field.isRequired && !raw) {
        found[field.key] = `${field.label} is required`;
        continue;
      }
      if (!raw) continue;
      if (field.fieldType === "number") {
        if (field.isMoney) {
          if (rupeesToPaise(raw) === null) found[field.key] = "Enter an amount like 1250.50";
        } else if (!Number.isFinite(Number(raw))) {
          found[field.key] = "Enter a number";
        }
      }
    }
    return found;
  }, [fields, draft]);

  const setValue = useCallback((key: string, value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => new Set(prev).add(key));
  }, []);

  const save = useCallback(
    async (recordId: string | undefined) => {
      if (!recordId) return;
      const payload = fields
        .filter((field) => touched.has(field.key))
        .map((field) => {
          const raw = (draft[field.key] ?? "").trim();
          if (!raw) return { fieldKey: field.key, value: null };
          if (field.fieldType === "number") {
            // Money crosses this boundary as PAISE, like every amount in AIM.
            return {
              fieldKey: field.key,
              value: field.isMoney ? (rupeesToPaise(raw) as number) : Number(raw),
            };
          }
          return { fieldKey: field.key, value: raw };
        });
      if (payload.length === 0) return;
      await saveValues(entityType, recordId, payload);
    },
    [fields, draft, touched, entityType],
  );

  return {
    fields,
    draft,
    setValue,
    errors,
    isValid: Object.keys(errors).length === 0,
    save,
  };
}

const INPUT =
  "min-h-10 w-full rounded-[var(--cp-r-control)] border border-[var(--cp-line)] bg-[var(--cp-surface)] px-3 py-2 text-[12.5px] text-[var(--cp-ink)] placeholder:text-[var(--cp-ink-3)] focus:border-[var(--cp-accent)] focus:outline-none focus:ring-[3px] focus:ring-[var(--cp-accent-wash)]";

export function ExtraFields({ state }: { state: ExtraFieldsState }) {
  if (state.fields.length === 0) return null;

  return (
    <div className="flex flex-col gap-2.5 border-t border-[var(--cp-line)] pt-3">
      {state.fields.map((field) => {
        const id = `wallet-cf-${field.key}`;
        const error = state.errors[field.key];
        const value = state.draft[field.key] ?? "";
        return (
          <div key={field.key}>
            <label
              htmlFor={id}
              className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[var(--cp-ink-3)]"
            >
              {field.label}
              {field.isRequired && <span className="text-[var(--cp-rose-ink)]"> *</span>}
              {field.isMoney && <span className="ml-1 normal-case">(₹)</span>}
            </label>
            {field.fieldType === "option_set" ? (
              <select
                id={id}
                value={value}
                onChange={(e) => state.setValue(field.key, e.target.value)}
                className={cn(INPUT, "pr-8")}
              >
                <option value="">—</option>
                {field.options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={id}
                type={field.fieldType === "date" ? "date" : "text"}
                inputMode={field.fieldType === "number" ? "decimal" : undefined}
                value={value}
                onChange={(e) => state.setValue(field.key, e.target.value)}
                placeholder={field.description ?? undefined}
                className={cn(INPUT, error && "border-[var(--cp-rose)]")}
              />
            )}
            {error && (
              <p className="mt-1 text-[11px] text-[var(--cp-rose-ink)]">{error}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
